import { Suspense, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { differenceInHours } from "date-fns";

import Loading from "../../../../common/components/ui/loading/Loading";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import {
  TableFilters,
  TableFiltersState,
} from "../../../../common/components/ui/table/TableFilters";
import GenericTable from "../../../../common/components/ui/table/GenericTable";
import { formatDateAndTime } from "../../../../common/helpers/date.helpers";
import TableFooter from "../../../../common/components/ui/table/TableFooter";

import {
  ILead,
  LEAD_STATUS,
  LeadType,
  SITUATION_ORIGIN_LEADS,
  StatusCounterLead,
} from "../../../../types/lead.type";
import {
  getLeads,
  startConversationVibechat,
} from "../../../../common/services/leads.api";
import ModalCreateLead from "../modals/CreateLead";
import Pendencies from "../PendenciesLead";
import ModalEditLead from "../modals/EditLead";
import ModalConvertLead from "../modals/ConvertLead";
import ModalEditAmbassador from "../../../ambassador/modals/EditAmbassador";
import { getCompanyId } from "../../../../common/services/company.api";
import { AuthContext } from "../../../../common/components/auth/authContext/AuthContext";
import { getAmbassadorByEmail } from "../../../../common/services/ambassador";
import { IIndicators } from "../../../../types/Indicators.type";
import ModalConfirmEdit from "../modals/ConfirmEdit";
import { useCompanyStore } from "../../../../common/states/store/Company";
import ModalArchiveLead from "../modals/ArchiveLead";
import ModalRestoreLead from "../modals/RestoreLead";
import CommercialActionsLeads from "../modals/CommercialActionsLead";
import { getCompanySigla } from "../../../../common/helpers/company.helpers";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { AiOutlineExclamationCircle } from "react-icons/ai";
import { BsExclamationCircleFill } from "react-icons/bs";
import { getEmployeeConsultant } from "@/common/services/employee.api";
import { ConsultantOption } from "@/types/employee.type";

const SITUATION_ORIGIN_LABELS = {
  [SITUATION_ORIGIN_LEADS.EMBAIXADOR]: "Embaixador",
  [SITUATION_ORIGIN_LEADS.FACEBOOK]: "Facebook",
  [SITUATION_ORIGIN_LEADS.GOOGLE]: "Google",
  [SITUATION_ORIGIN_LEADS.INDICACAO]: "Indicação",
  [SITUATION_ORIGIN_LEADS.INSTAGRAM]: "Instagram",
  [SITUATION_ORIGIN_LEADS.OLX]: "Olx",
  [SITUATION_ORIGIN_LEADS.RADIO]: "Rádio",
  [SITUATION_ORIGIN_LEADS.SITE]: "Site",
  [SITUATION_ORIGIN_LEADS.TELEVISAO]: "Televisão",
  [SITUATION_ORIGIN_LEADS.TIKTOK]: "TikTok",
  [SITUATION_ORIGIN_LEADS.SDR]: "SDR",
  [SITUATION_ORIGIN_LEADS.UNID_MOVEL]: "Unid. Móvel",
};

const STATUS_LABELS = {
  [LEAD_STATUS.NewLead]: "Novo Lead",
  [LEAD_STATUS.Incomplete]: "Em negociação",
  [LEAD_STATUS.ReadyToRent]: "Validado",
  [LEAD_STATUS.Converted]: "Convertido",
  [LEAD_STATUS.Archived]: "Arquivado",
  [LEAD_STATUS.UnderNegotiation]: "Em Tratativa",
  [LEAD_STATUS.Reproved]: "Reprovado",
};

const STATUS_COLORS = {
  [LEAD_STATUS.NewLead]: "bg-bluePrimary text-white",
  [LEAD_STATUS.Incomplete]: "bg-yellowPrimary",
  [LEAD_STATUS.ReadyToRent]: "bg-greenPrimary text-white",
  [LEAD_STATUS.Converted]: "bg-yellowPrimary",
  [LEAD_STATUS.Reproved]: "bg-redPrimary text-white",
};

function getStatusLabels(lead: ILead) {
  const statusKey = lead.negociationSDR ? LEAD_STATUS.Incomplete : lead.status;

  const label = STATUS_LABELS[statusKey] ?? String(statusKey);
  const colorClass = STATUS_COLORS[statusKey] ?? "";

  return (
    <span
      className={`inline-block px-2 py-1 rounded-md text-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px] md:max-w-full ${colorClass}`}
      title={label}
    >
      {label}
    </span>
  );
}

function formatWhatsAppNumber(whatsapp: string) {
  const cleanedNumber = whatsapp.replace(/\D/g, "");
  if (cleanedNumber.length == 10) {
    return `(${cleanedNumber.slice(0, 2)}) 9${cleanedNumber.slice(
      2,
      7
    )}-${cleanedNumber.slice(7)}`;
  }
  if (cleanedNumber.length == 11) {
    return `(${cleanedNumber.slice(0, 2)}) ${cleanedNumber.slice(
      2,
      7
    )}-${cleanedNumber.slice(7)}`;
  }
}

function calculateElapsedTime(lead: LeadType): JSX.Element {
  const createdAt = new Date(lead.createdAt);
  const now = new Date();

  const diffMs = Math.abs(now.getTime() - createdAt.getTime());
  const totalMinutes = Math.floor(diffMs / (1000 * 60));

  let label = "";

  if (totalMinutes >= 1440) {
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);

    const daysStr = days === 1 ? `${days} dia` : `${days} dias`;
    const hoursStr =
      hours > 0 ? (hours === 1 ? `${hours} hora` : `${hours} horas`) : "";

    label = hoursStr ? `${daysStr}` : daysStr;
  } else if (totalMinutes >= 60) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const hoursStr = hours === 1 ? `${hours} hora` : `${hours} horas`;
    const minutesStr =
      minutes > 0
        ? minutes === 1
          ? `${minutes} minuto`
          : `${minutes} minutos`
        : "";

    label = minutesStr ? `${hoursStr} e ${minutesStr}` : hoursStr;
  } else {
    label = totalMinutes === 1 ? "1 minuto" : `${totalMinutes} minutos`;
  }

  const shouldBlink = totalMinutes > 5 && lead.status === LEAD_STATUS.NewLead;
  const className = shouldBlink
    ? "animate-pulse-text text-red-600 font-bold"
    : "";

  return <span className={className}>{label}</span>;
}

const EMPTY_ACTION = { eventName: "", data: undefined };

const headers = {
  companyId: "Cidade",
  whatsapp: "WhatsApp",
  name: "Nome",
  technicalConsultant: "Consultor",
  ambassadorName: "Embaixador",
  pendencies: "Pendências cadastrais",
  validation: "Validação jurídica",
  status: "Status",
  leadOrigin: "Origem",
  createdAt: "Criado em",
  days: "Tempo",
};

function TableLeads() {
  const { auth } = useContext(AuthContext);
  const [consultantOptions, setConsultantOptions] = useState<
    ConsultantOption[]
  >([]);
  const [consultantNamesList, setConsultantNamesList] = useState<
    string[] | null
  >(null);
  const companyId = getCompanyId();
  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [leads, setLeads] = useState<ILead[]>([]);
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [isConsultant, setIsConsultant] = useState(false);

  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    leadOrigin: "",
    status: [],
    filterColumn: "",
    orderBy: "-updatedAt",
    filterMyLeads: isConsultant,
  });

  const [dataAmbassador, setDataAmbassador] = useState<IIndicators>();
  const [isAmbassador, setAmbassador] = useState(false);
  const [pendingDataAmbassador, setPendingDataAmbassador] = useState(false);
  const [statusCounter, setStatusCounter] = useState<StatusCounterLead>({
    new_lead: 0,
    incomplete: 0,
    ready_to_rent: 0,
    converted: 0,
    archived: 0,
    convertedLeads: 0,
    under_negotiation: 0,
  });

  const company = useCompanyStore();
  useEffect(() => {
    if (isConsultant) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        filterMyLeads: true,
      }));
    }
  }, [isConsultant]);

  useEffect(() => {
    async function loadLeads(filters, page) {
      if (consultantNamesList === null) {
        return; // Aborta a execução se os consultores ainda não foram carregados
      }
      setIsLoading(true);
      setLeads([]);

      const apiParams = {
        ...filters,
        page,
      };
      if (!isConsultant || filters.filterMyLeads === false) {
        delete apiParams.technicalConsultant;
      }
      const result = await getLeads(apiParams);

      setStatusCounter({
        new_lead: result.statusCounter.new_lead,
        incomplete: result.statusCounter.incomplete,
        ready_to_rent: result.statusCounter.ready_to_rent,
        converted: result.statusCounter.converted,
        archived: result.statusCounter.archived,
        convertedLeads: result.statusCounter.convertedLeads,
        under_negotiation: result.statusCounter.under_negotiation,
      });

      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });

      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        return;
      }

      setLeads(result.data);
      setIsLoading(false);
    }

    loadLeads(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters,
    page,
    notifyError,
    companyId,
    company,
    auth?.user?.name,
    consultantNamesList,
  ]);

  useEffect(() => {
    const fetchConsultants = async () => {
      setIsLoading(true);
      const result = await getEmployeeConsultant();

      if (result.ok && Array.isArray(result.data)) {
        const names = result.data.map((employee) => employee.name);

        const userIsConsultant = auth?.user
          ? names.includes(auth.user.name)
          : false;

        setIsConsultant(userIsConsultant);
        setConsultantNamesList(names);

        const formattedOptions = result.data.map((employee) => ({
          value: employee.name,
          label: employee.name,
        }));

        setConsultantOptions([
          { value: "", label: "Selecione..." },
          ...formattedOptions,
        ]);
        setIsLoading(false);
      }
    };

    fetchConsultants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user?.name]);

  useEffect(() => {
    if (!auth || !auth.user || !auth.user.roles) {
      setAmbassador(false);
      return;
    }

    const isAmbassador =
      auth.user.roles.length === 1 && auth.user.roles.includes("ambassador");
    setAmbassador(isAmbassador);

    if (isAmbassador) {
      const email = auth.user.email || "";

      const getDataAmbassador = async (email) => {
        setIsLoading(true);
        try {
          const result = await getAmbassadorByEmail(email);

          if (!result.ok) {
            notifyError(result.message || "Unknown error");
            return;
          }

          const data = result.data?.[0];
          if (data?.cpf === "") {
            setPendingDataAmbassador(true);
          }
          setDataAmbassador(data);
        } catch (error) {
          notifyError("Error fetching ambassador data");
        } finally {
          setIsLoading(false);
        }
      };

      getDataAmbassador(email);
    }
  }, [auth, notifyError]);

  const actions = [
    {
      label: "Editar",
      action: (data) =>
        setAction({ eventName: "edit", data: (data as LeadType)._id }),
      disabled: (data) =>
        ![
          LEAD_STATUS.Incomplete,
          LEAD_STATUS.ReadyToRent,
          LEAD_STATUS.NewLead,
          LEAD_STATUS.UnderNegotiation,
          "Indefinido",
        ].includes((data as LeadType).status as LEAD_STATUS),
      permission: "edit:leads",
    },
    {
      label: "Converter em cliente",
      action: (data) => setAction({ eventName: "convert", data: data }),
      disabled: (data) => LEAD_STATUS.ReadyToRent !== (data as LeadType).status,
      permission: "convertLead:renters",
    },
    {
      label: "Arquivar",
      action: (data) =>
        setAction({ eventName: "archive", data: (data as LeadType)._id }),
      disabled: (data) =>
        LEAD_STATUS.Converted == (data as LeadType).status ||
        LEAD_STATUS.Archived == (data as LeadType).status,
      permission: "edit:leads",
    },
    {
      label: "Restaurar",
      action: (data) =>
        setAction({ eventName: "restore", data: (data as LeadType)._id }),
      disabled: (data) => LEAD_STATUS.Archived !== (data as LeadType).status,
      permission: "edit:leads",
    },

    {
      label: "Ações Comerciais",
      action: (data) =>
        setAction({
          eventName: "commercialActions",
          data: {
            auth, // Inclui o auth completo
            leadId: (data as LeadType)._id, // Inclui o ID do locatário
          },
        }),
      disabled: () => !auth?.user.roles.includes("commercial-leader"),
      permission: "commercialActions:leads",
    },
  ];

  const isAction = (eventName: string) => action.eventName === eventName;

  const handleSearch = ({ search, status }) => {
    const newState = Object.assign({}, filters, { search, status });
    setFilters(newState);
  };

  const cleanActionData = () => {
    setAction(EMPTY_ACTION);
    handleSearch(filters);
  };

  function onLeadCreated(leadId: string) {
    handleSearch(filters);
    setAction({
      eventName: "edit",
      data: leadId,
    });
  }

  function openConfirmEdit(leadData: ILead) {
    setAction({
      eventName: "confirmEdit",
      data: leadData,
    });
  }

  function openEditExistLead(leadId: string) {
    setAction({
      eventName: "edit",
      data: leadId,
    });
  }

  const updatedAmbassador = () => {
    setPendingDataAmbassador(false);
    cleanActionData();
    handleSearch(filters);
  };

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  const startConversationRenderElement = (lead: ILead) => {
    const chat = lead.externalReferences?.chat;

    const hasMoreThan24HoursPassed = chat?.lastMessageAt
      ? differenceInHours(new Date(), new Date(chat.lastMessageAt)) > 24
      : false;

    const handleStartConversation = async () => {
      const newTab = window.open("", "_blank");

      try {
        const conversation = await startConversationVibechat(
          lead._id as string
        );

        if (conversation?.data?.chatUrl) {
          newTab!.location.href = conversation.data.chatUrl;
        } else {
          newTab!.close();
          console.error("URL da conversa não encontrada");
        }
      } catch (error) {
        newTab!.close();
        console.error("Erro ao criar conversa:", error);
      }
    };

    if (!chat?.id || hasMoreThan24HoursPassed) {
      return (
        <a
          onClick={handleStartConversation}
          className="text-bluePrimary underline cursor-pointer"
        >
          {formatWhatsAppNumber(lead.whatsapp)}
        </a>
      );
    }

    return (
      <a
        href={`https://chat.locavibe.com.br/app/accounts/1/inbox/4/conversations/${chat.id}`}
        target="_blank"
        className="text-bluePrimary underline cursor-pointer"
        rel="noopener noreferrer"
      >
        {formatWhatsAppNumber(lead.whatsapp)}
      </a>
    );
  };

  return (
    <>
      {isAmbassador && dataAmbassador && pendingDataAmbassador && (
        <ModalEditAmbassador
          onClose={updatedAmbassador}
          data={dataAmbassador}
        />
      )}
      <Suspense fallback={<Loading />}>
        <div className="table-section">
          <TableFilters
            dashboardHeader="Leads cadastrados"
            placeholder="Digite o nome, o e-mail, o CPF ou a CNH."
            statusOptions={STATUS_LABELS}
            newItemLabel="Novo Lead"
            onNewItemClick={() =>
              setAction({ eventName: "create", data: undefined })
            }
            leadOriginOptions={SITUATION_ORIGIN_LABELS}
            onSearch={handleFilterChange}
            entity="leads"
            technicalConsultant={consultantOptions}
            tableData={leads}
            isUserConsultant={isConsultant}
          />

          {!isLoading && !isAmbassador && (
            <div className="grid grid-cols-2 gap-3 mt-1 mb-3 lg:grid-cols-5">
              {statusCounter.new_lead > 0 ? (
                <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-2 flex items-center hover:shadow-lg transition animate-pulse-color">
                  <h2 className="text-sm text-white uppercase w-1/2">
                    Novos Leads
                  </h2>
                  <p className="text-lg text-white w-1/2 text-right">
                    {statusCounter.new_lead}
                  </p>
                </div>
              ) : (
                <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-2 flex items-center hover:shadow-lg transition">
                  <h2 className="text-sm text-white uppercase w-1/2">
                    Novos Leads
                  </h2>
                  <p className="text-lg text-white w-1/2 text-right">
                    {statusCounter.new_lead || 0}
                  </p>
                </div>
              )}

              <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-2 flex items-center hover:shadow-lg transition">
                <h2 className="text-sm text-white uppercase w-1/2">
                  Em Negociação
                </h2>
                <p className="text-lg text-white w-1/2 text-right">
                  {statusCounter.incomplete || 0}
                </p>
              </div>
              <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-2 flex items-center hover:shadow-lg transition">
                <h2 className="text-sm text-white uppercase w-1/2">
                  Validados
                </h2>
                <p className="text-lg text-white w-1/2 text-right">
                  {statusCounter.ready_to_rent || 0}
                </p>
              </div>
              <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-2 flex items-center hover:shadow-lg transition">
                <h2 className="text-sm text-white uppercase w-1/2">
                  Em tratativa
                </h2>
                <p className="text-lg text-white w-1/2 text-right">
                  {statusCounter.under_negotiation || 0}
                </p>
              </div>
              <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-2 flex items-center hover:shadow-lg transition">
                <h2 className="text-sm text-white uppercase w-1/2">
                  Convertidos
                </h2>
                <p className="text-lg text-white w-1/2 text-right">
                  {statusCounter.convertedLeads || 0}
                </p>
              </div>
            </div>
          )}

          <GenericTable
            headers={headers}
            data={leads}
            actions={actions}
            setFilters={({ orderBy }) => {
              setFilters((prevFilters) => ({
                ...prevFilters,
                orderBy,
              }));
            }}
            renders={
              {
                companyId: (companyId) => {
                  return getCompanySigla(companyId) || "";
                },
                whatsapp: (_, lead: ILead) => {
                  return startConversationRenderElement(lead);
                },

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                validation: (_: any, lead: ILead): JSX.Element => {
                  const validated = lead.jurValidation?.validated;
                  const status = lead.jurValidation?.status;

                  if (validated === false) {
                    return (
                      <BsExclamationCircleFill
                        className="text-yellow-500 w-5 h-5 mx-auto"
                        title="Validação Pendente"
                      />
                    );
                  }

                  if (validated === true && status === true) {
                    return (
                      <FaCheckCircle
                        className="text-green-500 w-5 h-5 mx-auto"
                        title="Validação Aprovada"
                      />
                    );
                  }

                  if (validated === true && status === false) {
                    return (
                      <FaTimesCircle
                        className="text-red-500 w-5 h-5 mx-auto"
                        title="Validação Reprovada"
                      />
                    );
                  }

                  return (
                    <AiOutlineExclamationCircle
                      className="text-gray-400 w-5 h-5 mx-auto"
                      title="Validação Indefinida"
                    />
                  );
                },

                name: (name, leads) =>
                  isAmbassador ? (
                    <span>{name}</span>
                  ) : (
                    <a
                      href={`/leads/${(leads as ILead)._id}`}
                      onClick={(e) => {
                        e.preventDefault(); // Para evitar o comportamento padrão do link
                        navigate(`/leads/${(leads as ILead)._id}`); // Navegação na mesma página
                      }}
                      target="_blank" // Abre em uma nova aba
                      rel="noopener noreferrer" // Melhoria de segurança
                    >
                      {name as string}
                    </a>
                  ),
                pendencies: (_, lead) => <Pendencies lead={lead as LeadType} />,
                createdAt: formatDateAndTime,
                status: (_, lead) => getStatusLabels(lead),
                leadOrigin: (leadOrigin) => leadOrigin || "Indefinido",
                days: (_, lead) => calculateElapsedTime(lead as LeadType),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            }
            entity="leads"
          />

          {isLoading && <Loading />}

          <TableFooter
            page={page || 1}
            totalItems={data.totalItems || 0}
            itemsPerPage={+(filters.itemsPerPage ?? 100)}
            goToPage={setPage}
          />

          {isAction("create") && (
            <ModalCreateLead
              roles={auth?.user.roles}
              openConfirmEdit={openConfirmEdit}
              onClose={cleanActionData}
              onSave={onLeadCreated}
            />
          )}
          {isAction("confirmEdit") && (
            <ModalConfirmEdit
              openEditExistLead={openEditExistLead}
              lead={action.data as ILead}
            ></ModalConfirmEdit>
          )}
          {isAction("edit") && (
            <ModalEditLead
              leadId={action.data as string}
              onClose={cleanActionData}
              ambassador={dataAmbassador}
            />
          )}
          {isAction("convert") && (
            <ModalConvertLead
              lead={action.data as { _id: string; name: string }}
              onClose={cleanActionData}
            />
          )}
          {isAction("archive") && (
            <ModalArchiveLead
              lead={action.data as string}
              onClose={cleanActionData}
            />
          )}
          {isAction("restore") && (
            <ModalRestoreLead
              lead={action.data as string}
              onClose={cleanActionData}
            />
          )}
          {isAction("commercialActions") && (
            <CommercialActionsLeads
              data={action.data as { auth: string; leadId: string }}
              onClose={cleanActionData}
            />
          )}
        </div>
      </Suspense>
    </>
  );
}

export default TableLeads;
